import React, { Component } from 'react';
/**
 * MetadataInfo Component
 *
 * This component provides the recommended meta tags for the FluidWeb landing page.
 * These tags should be added to your index.html file in the <head> section.
 *
 * Since Magic Patterns doesn't allow direct editing of index.html, you'll need to:
 * 1. Use the DEPLOY button in the top right
 * 2. Add these meta tags to your deployed site's HTML
 * 3. Upload your generated preview GIF and update the og:image URL
 */
const MetadataInfo = () => {
  const metaTags = `
<!-- Primary Meta Tags -->
<title>FluidWeb - Turn Your App Into an AI App</title>
<meta name="title" content="FluidWeb - Turn Your App Into an AI App" />
<meta name="description" content="The AI UX layer for your product. Drop in voice, chat, forms, and intelligent UI components that take action, navigate, and extract data." />

<!-- Open Graph / Facebook -->
<meta property="og:type" content="website" />
<meta property="og:url" content="https://fluid-form.ai/" />
<meta property="og:title" content="FluidWeb - Turn Your App Into an AI App" />
<meta property="og:description" content="The AI UX layer for your product. Drop in voice, chat, forms, and intelligent UI components that take action, navigate, and extract data." />
<meta property="og:image" content="https://your-cdn.com/fluidweb-preview.gif" />

<!-- Twitter -->
<meta property="twitter:card" content="summary_large_image" />
<meta property="twitter:url" content="https://fluid-form.ai/" />
<meta property="twitter:title" content="FluidWeb - Turn Your App Into an AI App" />
<meta property="twitter:description" content="The AI UX layer for your product. Drop in voice, chat, forms, and intelligent UI components that take action, navigate, and extract data." />
<meta property="twitter:image" content="https://your-cdn.com/fluidweb-preview.gif" />

<!-- Additional Meta Tags -->
<meta name="keywords" content="AI UX, voice interface, chat interface, AI forms, React SDK, TypeScript, AI components, conversational UI" />
<meta name="author" content="FluidWeb, Inc." />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<meta name="theme-color" content="#f97316" />

<!-- Favicon -->
<!-- Upload your favicon using the DEPLOY button in Magic Patterns -->
<link rel="icon" type="image/png" href="/favicon.png" />
<link rel="apple-touch-icon" href="/apple-touch-icon.png" />
  `.trim();
  return <div className="w-full min-h-screen bg-gray-50 py-12 px-4" data-id="element-726">
      <div className="max-w-4xl mx-auto" data-id="element-727">
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8" data-id="element-728">
          <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-729">
            📋 Metadata Setup Instructions
          </h1>
          <p className="text-gray-600 mb-6" data-id="element-730">
            Follow these steps to add preview images and metadata to your
            FluidWeb landing page:
          </p>

          <div className="space-y-6" data-id="element-731">
            <div className="border-l-4 border-orange-500 pl-4" data-id="element-732">
              <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-733">
                Step 1: Generate Preview GIF
              </h2>
              <ol className="list-decimal list-inside space-y-2 text-gray-700" data-id="element-734">
                <li data-id="element-735">
                  Navigate to{' '}
                  <code className="bg-gray-100 px-2 py-1 rounded" data-id="element-736">
                    /preview
                  </code>{' '}
                  route
                </li>
                <li data-id="element-737">
                  Use a screen recording tool (like OBS, QuickTime, or browser
                  extensions)
                </li>
                <li data-id="element-738">Record the animation for 10-15 seconds</li>
                <li data-id="element-739">Convert to GIF using ezgif.com or ffmpeg</li>
                <li data-id="element-740">Optimize the GIF to be under 5MB</li>
                <li data-id="element-741">Upload to your CDN or image hosting service</li>
              </ol>
            </div>

            <div className="border-l-4 border-blue-500 pl-4" data-id="element-742">
              <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-743">
                Step 2: Add Meta Tags
              </h2>
              <p className="text-gray-700 mb-3" data-id="element-744">
                Click the <strong data-id="element-745">DEPLOY</strong> button in the top right of
                Magic Patterns. Then add these meta tags to your deployed site's
                HTML{' '}
                <code className="bg-gray-100 px-2 py-1 rounded" data-id="element-746">
                  &lt;head&gt;
                </code>{' '}
                section:
              </p>
            </div>

            <div className="border-l-4 border-green-500 pl-4" data-id="element-747">
              <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-748">
                Step 3: Update Image URL
              </h2>
              <p className="text-gray-700" data-id="element-749">
                Replace{' '}
                <code className="bg-gray-100 px-2 py-1 rounded" data-id="element-750">
                  https://your-cdn.com/fluidweb-preview.gif
                </code>{' '}
                with your actual GIF URL in the meta tags below.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 rounded-xl shadow-lg p-6" data-id="element-751">
          <div className="flex items-center justify-between mb-4" data-id="element-752">
            <h2 className="text-lg font-semibold text-white" data-id="element-753">
              Meta Tags (Copy & Paste)
            </h2>
            <button onClick={() => {
            navigator.clipboard.writeText(metaTags);
            alert('Meta tags copied to clipboard!');
          }} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-id="element-754">
              Copy to Clipboard
            </button>
          </div>
          <pre className="text-sm text-gray-300 overflow-x-auto" data-id="element-755">
            <code data-id="element-756">{metaTags}</code>
          </pre>
        </div>

        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6" data-id="element-757">
          <h3 className="text-lg font-semibold text-blue-900 mb-2" data-id="element-758">
            💡 Pro Tips
          </h3>
          <ul className="space-y-2 text-blue-800" data-id="element-759">
            <li data-id="element-760">• Use a 1200x630px image for optimal social media display</li>
            <li data-id="element-761">• GIFs work great for showing product interactions</li>
            <li data-id="element-762">
              • Test your meta tags using Facebook's Sharing Debugger and
              Twitter Card Validator
            </li>
            <li data-id="element-763">• Keep GIF file size under 5MB for faster loading</li>
            <li data-id="element-764">• Consider creating both a GIF and static PNG version</li>
          </ul>
        </div>
      </div>
    </div>;
};
export default MetadataInfo;